import { CellClassParams, CellStyle, ColDef, ValueFormatterFunc } from 'ag-grid-community';

import { PriceListSubfamily } from '@mitsubishi/pricing-list/data-access';
import { getClassColor } from '@mitsubishi/shared/ui';
import {
  ApmGridColumnType,
  TableResourceHeaderComponent,
  TableResourceHeaderComponentParams,
  TableSplitCellComponent,
} from '@mitsubishi/ui/table';
import { ApmDateUtils } from '@mitsubishi/utils';

import { PriceListDetailsCellComponent } from './components/price-list-details-cell/price-list-details-cell.component';
import { PriceListSubfamilyGroupCellComponent } from './components/price-list-subfamily-group-cell/price-list-subfamily-group-cell.component';

// A cellStyle FUNCTION replaces the cellStyle object coming from defaultColDef + colDef `type`
// (ag-grid mergeDeep treats a function as non-mergeable), so ApmGridColumnType.CENTERED_CONTENT's
// centering has to be restated here.
const GROUP_CELL_BASE_STYLE: CellStyle = {
  'text-align': 'center',
  display: 'flex',
  'justify-content': 'center',
  'align-items': 'center',
};

/**
 * DoR 019: identifying class color as a left band on the classId grouping row (level 0) only.
 * Painted as a background gradient rather than a `border-left`: ag-grid's theme puts
 * `border: 1px solid transparent` on `.ag-cell`, and the transparent top/bottom borders clip the ends
 * of a left border, leaving a gap between the bands of consecutive rows. A background fills the whole
 * border box, so it stays continuous.
 */
export const classGroupBarCellStyle = (params: CellClassParams<PriceListSubfamily>): CellStyle => {
  const classId = params.node.level === 0 ? params.node.allLeafChildren?.[0]?.data?.classId : undefined;
  const color = getClassColor(classId);
  const bandWidth = 'var(--apm-accent-bar-width)';
  return {
    ...GROUP_CELL_BASE_STYLE,
    // explicit '' (not omission) so recycled cells at other levels drop a stale inline band
    'background-image': color
      ? `linear-gradient(to right, ${color} 0px, ${color} ${bandWidth}, transparent ${bandWidth})`
      : '',
  };
};

export const getPriceListGroupColumns = (): ColDef[] => [
  {
    field: 'classId',
    rowGroup: true,
    hide: true,
  },
  {
    field: 'subclassId',
    rowGroup: true,
    hide: true,
  },
];

export const getPriceListGroupAutoColumnDef = (): ColDef => ({
  headerName: '',
  minWidth: 450,
  field: 'subfamilyId',
  flex: 3,
  sortable: false,
  suppressMenu: true,
  autoHeight: true,
  type: ApmGridColumnType.CENTERED_CONTENT,
  cellStyle: classGroupBarCellStyle,
  cellRendererParams: {
    suppressCount: true,
    innerRenderer: PriceListSubfamilyGroupCellComponent,
    hideActions: true,
    showAlerts: false,
  },
});

export const getPriceListSubfamiliesColumns = (valueFormatter?: string | ValueFormatterFunc | undefined): ColDef[] => [
  {
    headerComponent: TableResourceHeaderComponent,
    headerComponentParams: {
      resourceId: '445582',
    } as TableResourceHeaderComponentParams,
    field: 'priceList',
    wrapText: true,
    autoHeight: true,
    sortable: false,
    suppressMenu: true,
    minWidth: 120,
    flex: 1,
    type: [ApmGridColumnType.CENTERED_CONTENT, ApmGridColumnType.PRE_WRAP],
    cellStyle: { 'line-height': 1.5 },
    cellRenderer: PriceListDetailsCellComponent,
  },
  {
    headerComponent: TableResourceHeaderComponent,
    headerComponentParams: {
      resourceId: '445583',
    } as TableResourceHeaderComponentParams,
    field: 'nextPriceList',
    wrapText: true,
    autoHeight: true,
    sortable: false,
    suppressMenu: true,
    minWidth: 120,
    flex: 1,
    type: [ApmGridColumnType.CENTERED_CONTENT, ApmGridColumnType.PRE_WRAP],
    cellStyle: { 'line-height': 1.5 },
    cellRenderer: PriceListDetailsCellComponent,
  },
];

export const getPriceListSubfamilyMarkupColumns = (dateFormat: string): ColDef[] => [
  {
    headerComponent: TableResourceHeaderComponent,
    headerComponentParams: {
      resourceId: '445585',
    } as TableResourceHeaderComponentParams,
    field: 'markup',
    cellDataType: 'text',
    wrapText: true,
    autoHeight: true,
    sortable: false,
    suppressMenu: true,
    minWidth: 110,
    flex: 1,
    cellStyle: { 'padding-left': 0, 'padding-right': 0, border: 0 },
    type: [ApmGridColumnType.CENTERED_CONTENT, ApmGridColumnType.PRE_WRAP],
    cellRenderer: TableSplitCellComponent,
    cellRendererParams: {
      topKey: 'markup.startValidityDate',
      bottomKey: 'nextMarkup.startValidityDate',
      formatValue: (value: string) => ApmDateUtils.getFormattedDate(value, dateFormat),
    },
  },
  {
    headerComponent: TableResourceHeaderComponent,
    headerComponentParams: {
      resourceId: '445586',
    } as TableResourceHeaderComponentParams,
    field: 'markup',
    cellDataType: 'text',
    wrapText: true,
    autoHeight: true,
    sortable: false,
    suppressMenu: true,
    minWidth: 110,
    flex: 1,
    type: [ApmGridColumnType.CENTERED_CONTENT, ApmGridColumnType.PRE_WRAP],
    cellStyle: { 'padding-left': 0, 'padding-right': 0, border: 0 },
    cellRenderer: TableSplitCellComponent,
    cellRendererParams: {
      topKey: 'markup.machineValue',
      bottomKey: 'nextMarkup.machineValue',
      formatValue: (value: number) => `${parseFloat((value * 100).toFixed(4))}%`,
    },
  },
  {
    headerComponent: TableResourceHeaderComponent,
    headerComponentParams: {
      resourceId: '445587',
    } as TableResourceHeaderComponentParams,
    field: 'markup',
    cellDataType: 'text',
    wrapText: true,
    autoHeight: true,
    sortable: false,
    suppressMenu: true,
    minWidth: 110,
    flex: 1,
    cellStyle: { 'padding-left': 0, 'padding-right': 0, border: 0 },
    type: [ApmGridColumnType.CENTERED_CONTENT, ApmGridColumnType.PRE_WRAP],
    cellRenderer: TableSplitCellComponent,
    cellRendererParams: {
      topKey: 'markup.accessoryValue',
      bottomKey: 'nextMarkup.accessoryValue',
      formatValue: (value: number) => `${parseFloat((value * 100).toFixed(4))}%`,
    },
  },
];
